"use client"

import {
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FileZipOutlined,
  SoundOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons"
import type { MediaData } from "../../../../lib/config/types"

export const isImageFile = (media: Pick<MediaData, "mime">) => (media.mime || "").toLowerCase().startsWith("image/")
export const isVideoFile = (media: Pick<MediaData, "mime">) => (media.mime || "").toLowerCase().startsWith("video/")

type FileKind = "pdf" | "word" | "excel" | "ppt" | "text" | "zip" | "audio" | "image" | "video" | "file"

// Phân loại file theo mime, fallback theo phần mở rộng trong tên (mime đôi khi
// thiếu hoặc là application/octet-stream sau khi upload).
const classifyFile = (mime?: string, name?: string): FileKind => {
  const m = (mime || "").toLowerCase()
  const ext = (name?.split(".").pop() || "").toLowerCase()

  if (m.startsWith("image/")) return "image"
  if (m.startsWith("video/")) return "video"
  if (m.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a", "flac"].includes(ext)) return "audio"
  if (m === "application/pdf" || ext === "pdf") return "pdf"
  if (m.includes("word") || ["doc", "docx", "rtf", "odt"].includes(ext)) return "word"
  if (m.includes("sheet") || m.includes("excel") || ["xls", "xlsx", "csv", "ods"].includes(ext)) return "excel"
  if (m.includes("presentation") || m.includes("powerpoint") || ["ppt", "pptx", "odp"].includes(ext)) return "ppt"
  if (m.startsWith("text/") || ["txt", "md", "log", "json", "xml"].includes(ext)) return "text"
  if (
    m.includes("zip") ||
    m.includes("rar") ||
    m.includes("7z") ||
    m.includes("compressed") ||
    ["zip", "rar", "7z", "tar", "gz"].includes(ext)
  )
    return "zip"
  return "file"
}

const ICON_BY_KIND: Record<FileKind, { Icon: React.ComponentType<{ style?: React.CSSProperties }>; color: string }> = {
  pdf: { Icon: FilePdfOutlined, color: "#ff4d4f" },
  word: { Icon: FileWordOutlined, color: "#2b579a" },
  excel: { Icon: FileExcelOutlined, color: "#217346" },
  ppt: { Icon: FilePptOutlined, color: "#d24726" },
  text: { Icon: FileTextOutlined, color: "#8c8c8c" },
  zip: { Icon: FileZipOutlined, color: "#faad14" },
  audio: { Icon: SoundOutlined, color: "#722ed1" },
  image: { Icon: FileImageOutlined, color: "#1677ff" },
  video: { Icon: VideoCameraOutlined, color: "#13c2c2" },
  file: { Icon: FileOutlined, color: "#8c8c8c" },
}

/**
 * Icon đại diện cho file không phải ảnh/video trong media list (PDF, Word, Excel,
 * PowerPoint, text, zip, audio…). Trước đây các file này bị đổ vào <Image> nên
 * hiện icon ảnh vỡ.
 */
export default function FileTypeIcon({
  mime,
  name,
  fontSize = 28,
}: {
  mime?: string
  name?: string
  fontSize?: number
}) {
  const { Icon, color } = ICON_BY_KIND[classifyFile(mime, name)]
  return <Icon style={{ fontSize, color }} />
}
